import { GridBaseDataProvider } from '../../../../grid/GridBaseDataProvider';
import { GetRowsRequest, GetSummaryResponse } from '../../../../grid/types/common';
import {
  ColumnIndexes,
  ColumnKeys,
  DEFAULT_COLUMN_VISIBILITY_FOR_MONTHLY,
  DEFAULT_COLUMN_VISIBILITY_FOR_YEARLY,
} from '../types/licensing-grid-columns';
import { GetSummaryRequest, SetFiltersRequest, UpdateSettingsRequest } from '../types/licensing-grid-requests';
import { GetLicensesResponse, LicenseUsageDetailsResponse } from '../types/licensing-grid-responses';
import { LicenseUsage } from '../types/licensing-grid-license';
import _ from 'lodash';
import { MonthlyProductType } from '../../../../license/types/MonthlyProductType';

export class LicenseUsageDetailsDataProvider extends GridBaseDataProvider<ColumnIndexes, ColumnKeys> {
  private readonly licenseUsageDetails: LicenseUsageDetailsResponse;

  constructor(public dependencies: any, licenseUsageDetails: LicenseUsageDetailsResponse) {
    super();
    this.licenseUsageDetails = licenseUsageDetails;
  }

  async getSummary(request: GetSummaryRequest): Promise<GetSummaryResponse> {
    return {
      summary: '',
      numItems: this.licenseUsageDetails.total,
    };
  }

  async getRows(request: GetRowsRequest<ColumnIndexes, ColumnKeys>): Promise<GetLicensesResponse> {
    return {
      success: true,
      rawEvents: this.convertLicensesToGridFormat(this.licenseUsageDetails),
    };
  }

  async updateSettings(request: UpdateSettingsRequest): Promise<{}> {
    return {};
  }

  async setFilters(request: SetFiltersRequest): Promise<{}> {
    return {};
  }

  public getVisibleColumns(licenseUsageDetails: LicenseUsageDetailsResponse, isPartner: boolean) {
    const licenses: Array<LicenseUsage> = (licenseUsageDetails.data as Array<LicenseUsage>) || [];
    const hasMonthly: boolean = licenseUsageDetails.hasMonthly;
    const visibleColumns: Array<number> = [];
    let columns;

    if (hasMonthly) {
      columns = _.cloneDeep(DEFAULT_COLUMN_VISIBILITY_FOR_MONTHLY);
      const monthlyLicense = licenses[0];
      if (monthlyLicense) {
        columns.protectionModel.isVisible = monthlyLicense.productType === MonthlyProductType.EndpointSecurity;
        columns.licenseKey.isVisible = !!monthlyLicense.licenseKey && !monthlyLicense.isInheriting;
        columns.expiryDate.isVisible = this.shouldExpiryDateBeVisibleForMonthly(
          columns.expiryDate.isVisible,
          monthlyLicense
        );
        columns.totalSeats.isVisible = !!monthlyLicense.totalSeats;
        columns.reservedSeats.isVisible = !!monthlyLicense.totalSeats && isPartner;
        columns.usedReservedSeats.isVisible = !!monthlyLicense.totalSeats;
        columns.availableReservedSeats.isVisible = !!monthlyLicense.totalSeats;
        columns.unreservedSeats.isVisible = !!monthlyLicense.totalSeats && isPartner;
        columns.usedUnreservedSeats.isVisible = !(!!monthlyLicense.totalSeats && !isPartner); // not displayed for customer with reserved seats
        columns.availableUnreservedSeats.isVisible = !!monthlyLicense.totalSeats && isPartner;
        columns.minimalCommitment.isVisible = !!monthlyLicense.minimalCommitment;
        columns.subscriptionEndDate.isVisible = !!monthlyLicense.subscriptionEndDate;
        columns.autoRenewal.isVisible = !!monthlyLicense.autoRenewal;
      }
    } else {
      columns = DEFAULT_COLUMN_VISIBILITY_FOR_YEARLY;
    }

    for (const column in columns) {
      if (columns[column].isVisible) {
        visibleColumns.push(columns[column].index);
      }
    }

    return visibleColumns;
  }

  private convertMonthlyLicense(rawLicense: { [key: string]: any }): Array<LicenseUsage> {
    return [
      {
        productId: rawLicense.productId,
        productType: rawLicense.productType,
        productStatus: rawLicense.productStatus,
        licenseKey: rawLicense.licenseKey,
        totalSeats: Math.max(0, rawLicense.totalSeats),
        reservedSeats: Math.max(0, rawLicense.reservedSeats),
        usedReservedSeats: Math.max(0, rawLicense.usedReservedSeats),
        availableReservedSeats: Math.max(0, rawLicense.availableReservedSeats),
        unreservedSeats: Math.max(0, rawLicense.unreservedSeats),
        usedUnreservedSeats: Math.max(0, rawLicense.usedUnreservedSeats),
        availableUnreservedSeats: Math.max(0, rawLicense.availableUnreservedSeats),
        minimalCommitment: Math.max(0, rawLicense.minimalCommitment),
        subscriptionEndDate: rawLicense.subscriptionEndDate,
        hasInheritedSubscriptionEndDate: rawLicense.hasInheritedSubscriptionEndDate,
        autoRenewal: rawLicense.autoRenewal,
        isInheriting: rawLicense.isInheriting,
        hasBasicMonthly: rawLicense.hasBasicMonthly,
        isMonthly: true,
        expiryDate: rawLicense.expiryDate,
        protectionModel: rawLicense.protectionModel,
      },
    ];
  }

  private convertLicensesToGridFormat(licenseUsageDetails): Array<LicenseUsage> {
    const licenses: Array<{ [key: string]: any }> = (licenseUsageDetails.data as Array<{ [key: string]: any }>) || [];

    if (licenseUsageDetails.hasMonthly) {
      return this.convertMonthlyLicense(licenses[0]);
    }

    return licenses.map(rawLicense => {
      return {
        productId: rawLicense.productId,
        productType: rawLicense.isAddon, // field must be present in order to trigger the renderer
        isAddon: rawLicense.isAddon,
        isTrial: rawLicense.isTrial,
        isInProductTrial: rawLicense.isInProductTrial,
        productStatus: rawLicense.productStatus,
        licenseKey: rawLicense.licenseKey,
        expiryDate: rawLicense.expiryDate,
        totalSeats: Math.max(0, rawLicense.totalSeats),
        usageBreakdown: rawLicense.usageBreakdown,
        isMonthly: false,
      };
    });
  }

  private shouldExpiryDateBeVisibleForMonthly(defaultVisibility: boolean, license: LicenseUsage) {
    if (license.hasMspTrial && license.expiryDate) {
      return !license.isInheriting; // show expiry date only if subscription is not inherited
    }
    return defaultVisibility;
  }
}
